import { splitProps, type JSX, type Component } from "solid-js";

/**
 * AplombButton — port TSX du composant Compose (6 variantes).
 *
 * ADR-0011 § composants noyau :
 * - Cible tactile min 44dp (44px web), défaut 48px.
 * - Action irréversible dit ce qu'elle coûte → prop `cost` optionnel injecté dans le label.
 * - Focus ring double-couche via base.css :focus-visible.
 */

export type AplombButtonVariant =
  | "primary"        // action principale
  | "secondary"      // action secondaire (contour cuivre)
  | "tertiary"       // texte seul
  | "destructive"    // suppression, annulation
  | "success"        // confirmation
  | "quiet";         // très discret

export type AplombButtonSize = "compact" | "regular" | "large";

export interface AplombButtonProps extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  variant?: AplombButtonVariant;
  size?: AplombButtonSize;
  /** Montant/quantité affiché à côté du label sur action irréversible. */
  cost?: string;
  loading?: boolean;
  fullWidth?: boolean;
}

export const AplombButton: Component<AplombButtonProps> = (rawProps) => {
  const [local, rest] = splitProps(rawProps, [
    "variant",
    "size",
    "cost",
    "loading",
    "fullWidth",
    "children",
    "class",
    "disabled",
  ]);

  const variant = () => local.variant ?? "primary";
  const size = () => local.size ?? "regular";
  const disabled = () => local.disabled || local.loading;

  return (
    <button
      class={`a-btn a-btn--${variant()} a-btn--${size()}${local.fullWidth ? " a-btn--fullwidth" : ""}${local.class ? ` ${local.class}` : ""}`}
      disabled={disabled()}
      aria-busy={local.loading ? "true" : undefined}
      {...rest}
    >
      {local.loading && <span class="a-btn__spinner" aria-hidden="true" />}
      <span class="a-btn__label">
        {local.children}
        {local.cost && <span class="a-btn__cost"> — {local.cost}</span>}
      </span>
    </button>
  );
};
